import { useState } from 'react';
import { DECKS } from '../../config/decks';
import { averageEase, chapterPerformance, dueTodayCount, hardestCards, maturityDistribution, retentionRate, reviewsToday, scheduleProgress, studyPace, type ScheduleProgress } from '../../domain/analytics/analytics';
import type { AppData, Card, DeckId } from '../../types';

interface Props {
  cards: Record<DeckId, Card[]>;
  data: AppData;
}

export function AnalyticsView({ cards, data }: Props) {
  const [deckId, setDeckId] = useState<DeckId | 'all'>('all');
  const selectedDecks = deckId === 'all' ? DECKS : DECKS.filter((deck) => deck.id === deckId);
  const deckCards = selectedDecks.flatMap((deck) => cards[deck.id] ?? []);
  const states = deckId === 'all'
    ? Object.assign({}, ...DECKS.map((deck) => data.decks[deck.id]?.cards ?? {}))
    : data.decks[deckId]?.cards ?? {};
  const maturity = maturityDistribution(deckCards, states);
  const chapters = deckId === 'all' ? {} : chapterPerformance(deckCards, states, data.history, deckId);
  const retention = retentionRate(data.history, 30, deckId === 'all' ? null : deckId);
  const ease = averageEase(deckCards, states);
  const weakCards = hardestCards(deckCards, states, 10);
  const totalMaturity = Object.values(maturity).reduce((sum, value) => sum + value, 0) || 1;
  const scheduleDecks = (deckId === 'all' ? DECKS : DECKS.filter((deck) => deck.id === deckId)).map((deck) => ({
    deck,
    progress: scheduleProgress(cards[deck.id] ?? [], data.history, deck.id, data.decks[deck.id]?.examDate ?? null),
  }));

  return (
    <section className="view active">
      <header className="page-head">
        <div><h1>Analyse</h1><p className="muted">Fortschritt, Reifegrad und schwache Kapitel.</p></div>
        <label className="select-wrap"><span className="sr-only">Fach wählen</span><select className="select" value={deckId} onChange={(event) => setDeckId(event.target.value as DeckId | 'all')}>
          <option value="all">Alle Fächer</option>
          {DECKS.map((deck) => <option key={deck.id} value={deck.id}>{deck.icon}  {deck.name}</option>)}
        </select></label>
      </header>
      <div className="deck-tabs" role="tablist" aria-label="Analyse-Fach wählen">
        <button className={`deck-tab ${deckId === 'all' ? 'active' : ''}`} onClick={() => setDeckId('all')}><span className="deck-tab-icon">📚</span><span className="deck-tab-name">Alle Fächer</span><span className="deck-tab-count">{deckCards.length}</span></button>
        {DECKS.map((deck) => <button key={deck.id} className={`deck-tab ${deckId === deck.id ? 'active' : ''}`} onClick={() => setDeckId(deck.id)}><span className="deck-tab-icon">{deck.icon}</span><span className="deck-tab-name">{deck.name}</span><span className="deck-tab-count">{cards[deck.id]?.length ?? 0}</span></button>)}
      </div>
      <div className="stats-grid">
        <Stat label="Retention (30T)" value={retention === null ? '–' : `${Math.round(retention * 100)}%`} />
        <Stat label="Reviews heute" value={reviewsToday(data.history, deckId === 'all' ? null : deckId)} />
        <Stat label="Ø Ease" value={ease === null ? '–' : ease.toFixed(2)} />
        <Stat label="Karten" value={deckCards.length} />
      </div>
      <div className="analytics-overview">
        {DECKS.map((deck) => {
          const deckStates = data.decks[deck.id]?.cards ?? {};
          const deckCardList = cards[deck.id] ?? [];
          const pace = studyPace(deckCardList, deckStates, data.decks[deck.id]?.examDate ?? null, data.settings);
          const due = dueTodayCount(deckCardList, deckStates);
          const deckRetention = retentionRate(data.history, 30, deck.id);
          return (
            <article key={deck.id} className="overview-card">
              <h3>{deck.icon} {deck.name}</h3>
              <div className="overview-metrics"><span>{due.total} fällig</span><span>{pace.newRemaining} neu</span><span>{Math.round(pace.maturePct * 100)}% reif</span><span>{deckRetention === null ? '–' : `${Math.round(deckRetention * 100)}%`} Retention</span></div>
              <div className="overview-maturity"><span style={{ width: `${Math.round(pace.learnedPct * 100)}%` }} /></div>
              <p className="overview-cta">{pace.daysUntilExam ? `${pace.daysUntilExam} Tage · ${pace.newPerDay} neue Karten/Tag empfohlen` : `${pace.newPerDay} neue Karten/Tag empfohlen`}</p>
            </article>
          );
        })}
      </div>
      <div className="panel schedule-panel">
        <div className="schedule-head">
          <div>
            <h2>Zeitplan</h2>
            <p className="muted">Bearbeitete Karten gegenüber dem idealen Lernpfad bis zur Klausur.</p>
          </div>
        </div>
        <div className="schedule-grid">
          {scheduleDecks.map(({ deck, progress }) => (
            <ScheduleCard key={deck.id} deckIcon={deck.icon} deckName={deck.name} progress={progress} />
          ))}
        </div>
      </div>
      <div className="grid-2 analytics-detail-grid">
        <div className="panel"><h2>Reife-Verteilung</h2>{Object.entries(maturity).map(([key, value]) => <div key={key} className="bar-row"><span>{key}</span><div className="progress"><span style={{ width: `${(value / totalMaturity) * 100}%` }} /></div><strong>{value}</strong></div>)}</div>
        <div className="panel"><h2>{deckId === 'all' ? 'Schwache Karten' : 'Kapitel'}</h2>{deckId === 'all' ? <HardestList items={weakCards} /> : <ChapterList chapters={chapters} />}</div>
        {deckId !== 'all' && <div className="panel"><h2>Schwächste Karten</h2><HardestList items={weakCards} /></div>}
      </div>
    </section>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return <div className="stat-card"><div className="stat-label">{label}</div><div className="stat-value">{value}</div></div>;
}

function ChapterList({ chapters }: { chapters: ReturnType<typeof chapterPerformance> }) {
  return <div className="chap-list">{Object.entries(chapters).sort((a, b) => a[1].maturePct - b[1].maturePct).map(([chapter, row]) => {
    const pct = Math.round(row.maturePct * 100);
    const warnClass = pct < 25 ? 'bad' : pct < 55 ? 'warn' : '';
    return <div key={chapter} className="chap-row"><div><strong>{chapter}</strong><span>{row.total} Karten · {row.retention === null ? 'keine Reviews' : `${Math.round(row.retention * 100)}% Retention`}</span></div><div className="chap-bar"><span className={`chap-bar-fill ${warnClass}`} style={{ width: `${pct}%` }} /></div><b>{pct}%</b></div>;
  })}</div>;
}

function HardestList({ items }: { items: ReturnType<typeof hardestCards> }) {
  if (!items.length) return <p className="muted">Noch keine problematischen Karten erkannt.</p>;
  return <div className="hardest-list">{items.map(({ card, state, accuracy }) => <div key={card.id} className="hardest-row"><div><div className="hardest-q">Karte {card.number}: {card.front}</div><div className="hardest-meta">{card.chapter} · {state?.lapses ?? 0} Lapses · {state?.reviewCount ?? 0} Reviews · {accuracy === null ? '–' : `${Math.round(accuracy * 100)}%`} korrekt</div></div></div>)}</div>;
}

function ScheduleCard({ deckIcon, deckName, progress }: { deckIcon: string; deckName: string; progress: ScheduleProgress }) {
  const dateFmt = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });
  const delta = Math.round(progress.delta);
  const status = progress.total === 0
    ? 'Keine Karten geladen.'
    : !progress.hasExam
      ? 'Kein Klausurdatum gesetzt – Standardzeitraum 30 Tage.'
      : delta >= 0
        ? `Du bist ${delta} Karten vor dem Plan.`
        : `Du liegst ${Math.abs(delta)} Karten hinter dem Plan.`;
  const statusClass = progress.total === 0 || !progress.hasExam ? 'neutral' : delta >= 0 ? 'good' : 'bad';
  return (
    <article className="schedule-card">
      <header className="schedule-card-head">
        <h3>{deckIcon} {deckName}</h3>
        <span className={`schedule-status ${statusClass}`}>{status}</span>
      </header>
      <ScheduleChart progress={progress} />
      <div className="schedule-meta">
        <span>{progress.learned} / {progress.total} bearbeitet</span>
        <span>Soll heute: {Math.round(progress.idealAtToday)}</span>
        <span>{dateFmt.format(progress.start)} → {dateFmt.format(progress.end)}</span>
      </div>
    </article>
  );
}

function ScheduleChart({ progress }: { progress: ScheduleProgress }) {
  const width = 320;
  const height = 120;
  const padX = 8;
  const padY = 8;
  const innerW = width - padX * 2;
  const innerH = height - padY * 2;
  const denomX = Math.max(1, progress.days - 1);
  const denomY = Math.max(1, progress.total);
  const x = (i: number) => padX + (i / denomX) * innerW;
  const y = (v: number) => padY + innerH - (v / denomY) * innerH;
  const idealPath = progress.points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(p.day).toFixed(2)},${y(p.ideal).toFixed(2)}`).join(' ');
  const actualSegment = progress.points.slice(0, progress.todayIndex + 1);
  const actualPath = actualSegment.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(p.day).toFixed(2)},${y(p.actual).toFixed(2)}`).join(' ');
  const areaPath = actualSegment.length > 0
    ? `${actualPath} L${x(actualSegment[actualSegment.length - 1].day).toFixed(2)},${y(0).toFixed(2)} L${x(0).toFixed(2)},${y(0).toFixed(2)} Z`
    : '';
  const todayX = x(progress.todayIndex);
  return (
    <svg className="schedule-chart" viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Lernfortschritt gegenüber dem geplanten Verlauf">
      <line x1={padX} x2={padX + innerW} y1={y(0)} y2={y(0)} className="schedule-axis" />
      {areaPath && <path d={areaPath} className="schedule-area" />}
      <path d={idealPath} className="schedule-ideal" />
      {actualPath && <path d={actualPath} className="schedule-actual" />}
      <line x1={todayX} x2={todayX} y1={padY} y2={padY + innerH} className="schedule-today" />
    </svg>
  );
}
